import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Link, Instagram, Youtube, Facebook, Globe } from "lucide-react";
import { TikTokIcon, XIcon, SubstackIcon, ThreadsIcon } from '../components/SocialIcons';
import RecipeCard from '../components/RecipeCard';
import RecipeCardSkeleton from '../components/RecipeCardSkeleton';
import RecipeCollectionGrid from '../components/RecipeCollectionGrid';
import { API } from '../utils/constants';
import recipeCache from '../utils/recipeCache';
import { getWithRetry, isNotFound } from '../utils/fetchRetry';
import { saveListReturnState, readListReturnState, clearListReturnState } from '../utils/listReturnState';
import { filterRecipesBySearch } from '../utils/recipeSearch';
import PublicCTA from '../components/PublicCTA';
import BetaForm from '../BetaForm';
import SaveRecipeOverlay from '../components/SaveRecipeOverlay';
import EmptyCollectionState from '../components/EmptyCollectionState';
import { normalizeSocialUrl } from '../lib/socialLinks';
import NotFound from './NotFound';
import RecipeCollection from './RecipeCollection';
import { isOwnPage } from '../utils/myPage';
import { getCollectionDisplayTitle } from '../utils/collectionTitle';
import { getAuthConfig } from '../utils/authSession';

const PublicCollectionView = () => {
  const { userId } = useParams();  // This can be either a userId (UUID) or username
  const navigate = useNavigate();
  const [recipes, setRecipes] = useState([]);
  const [filteredRecipes, setFilteredRecipes] = useState([]);
  // If the visitor is returning from a recipe they opened, restore the
  // category they had selected (and, below, their scroll position).
  const [returnState] = useState(() => readListReturnState(`public:${userId}`));
  const [selectedCategory, setSelectedCategory] = useState(returnState?.selectedCategory || "all");
  const [searchQuery, setSearchQuery] = useState("");
  const [categories, setCategories] = useState([]);
  const [profile, setProfile] = useState({
    collection_title: "Recipe Collection",
    bio: "",
    creator_name: "",
    creator_photo_url: "",
    username: ""
  });
  const [resolvedUserId, setResolvedUserId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [retryKey, setRetryKey] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  // Save-to-your-Wellby: ids of ORIGINAL recipes the logged-in viewer has
  // already saved (filled bookmark). Logged-out viewers get the waitlist
  // dialog with the recipe held as a pending save.
  const [savedOriginalIds, setSavedOriginalIds] = useState(() => new Set());
  const [pendingSaveRecipe, setPendingSaveRecipe] = useState(null);
  const [showWaitlist, setShowWaitlist] = useState(false);
  // Only trust a token once it has actually worked — a stale/expired
  // adminToken in localStorage must fall back to the waitlist flow.
  const [viewerVerified, setViewerVerified] = useState(false);
  const [saveOverlayRecipe, setSaveOverlayRecipe] = useState(null);
  const [authResolved, setAuthResolved] = useState(false);

  // A hard refresh can begin with only the refresh token in storage. Resolve
  // that session before deciding whether this URL is the owner's editable
  // collection or a public page, so owner data is never fetched/rendered via
  // the public route during the bootstrap window.
  useEffect(() => {
    let cancelled = false;
    getAuthConfig().finally(() => {
      if (!cancelled) setAuthResolved(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // Route changed under us (back/forward or navigating to another Wellby):
  // any open save overlay / waitlist belongs to the previous page — drop it
  // so it can't save the wrong recipe.
  useEffect(() => {
    setSaveOverlayRecipe(null);
    setShowWaitlist(false);
    setPendingSaveRecipe(null);
  }, [userId]);

  // If the viewer is logged in, learn which of this page's recipes they've
  // already saved so the bookmark shows filled.
  useEffect(() => {
    if (!authResolved) return;
    const token = localStorage.getItem('adminToken');
    if (!token || isOwnPage(userId)) return;
    axios.get(`${API}/recipes`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => {
        const ids = new Set(
          (res.data || []).map(r => r.saved_from_recipe_id).filter(Boolean)
        );
        setSavedOriginalIds(ids);
        setViewerVerified(true);
      })
      .catch(() => { /* token invalid/expired — treat viewer as logged out */ });
  }, [userId, authResolved]);

  // Viewing your own collection through an old handle or UUID link:
  // isOwnPage() misses aliases, so also compare resolved ids and hide the
  // save button (you can't save your own recipes).
  const storedUser = (() => {
    try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; }
  })();
  const isOwnCollection = !!(storedUser.id && resolvedUserId && storedUser.id === resolvedUserId);

  const handleSaveRecipe = async (recipe) => {
    const token = localStorage.getItem('adminToken');
    if (!token || !viewerVerified) {
      setPendingSaveRecipe(recipe);
      setShowWaitlist(true);
      return;
    }
    if (savedOriginalIds.has(recipe.id)) {
      toast.info("Already saved to your Wellby");
      return;
    }
    // Same flow as the recipe page: review/edit in the blurred overlay
    // before the copy is created.
    setSaveOverlayRecipe(recipe);
  };

  // Called just before navigating into a recipe so the return trip can
  // land back on the same category and scroll position.
  // Save under the username when we know it — recipe pages link back to
  // /<username>, so the remounted list looks the state up under that key
  // even if this page was reached via a legacy UUID link.
  const saveReturnState = () => {
    const key = `public:${profile.username || userId}`;
    saveListReturnState(key, { selectedCategory });
  };

  // Restore scroll position once the page content has actually rendered
  const scrollRestoredRef = React.useRef(false);
  useEffect(() => {
    if (loading || !returnState || scrollRestoredRef.current) return;
    scrollRestoredRef.current = true;
    clearListReturnState(`public:${userId}`);
    requestAnimationFrame(() => window.scrollTo(0, returnState.scrollY || 0));
  }, [loading, returnState]);

  // Detect mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (!authResolved) return;
    const fetchPublicData = async () => {
      if (isOwnPage(userId)) return; // owner sees RecipeCollection instead
      // Fresh request — clear stale outcome state from a previous URL
      setNotFound(false);
      setLoadError(false);
      setLoading(true);
      try {
        // Check if we have everything cached already
        const cachedProfile = recipeCache.getProfile(userId);
        const cacheKey = `public:${cachedProfile?.id || userId}`;
        const cachedRecipes = recipeCache.getList(cacheKey);
        
        // If we have both cached, use them immediately
        if (cachedProfile && cachedRecipes) {
          setProfile(cachedProfile);
          setResolvedUserId(cachedProfile.id);
          setRecipes(cachedRecipes);
          setFilteredRecipes(cachedRecipes);
          setLoading(false);
          // Categories still need to be present on this fast path — without
          // this, coming back to a profile from a recipe showed the page with
          // NO category tags until a hard refresh.
          const catKey = `categories:${cachedProfile.id}`;
          const cachedCats = recipeCache.getList(catKey);
          if (cachedCats) {
            setCategories(cachedCats);
          } else {
            axios.get(`${API}/categories?user_id=${cachedProfile.id}`)
              .then((res) => {
                const sorted = res.data.sort((a, b) => (a.order || 0) - (b.order || 0));
                recipeCache.setList(catKey, sorted);
                setCategories(sorted);
              })
              .catch(() => {});
          }
          return;
        }

        let profileData;
        let actualUserId = userId;
        
        // Check if it's a UUID (userId) or username
        const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(userId);
        
        if (cachedProfile) {
          // Use cached profile
          profileData = cachedProfile;
          actualUserId = cachedProfile.id;
        } else if (isUUID) {
          // Fetch by user ID
          const profileResponse = await getWithRetry(`${API}/profile?user_id=${userId}`);
          profileData = profileResponse.data;
          recipeCache.setProfile(userId, profileData);
        } else {
          // Try to fetch by username
          try {
            const profileResponse = await getWithRetry(`${API}/u/${userId}`);
            profileData = profileResponse.data;
            actualUserId = profileData.id;
            recipeCache.setProfile(userId, profileData);
          } catch (err) {
            // Fall back to the legacy lookup only on a genuine 404 —
            // transient failures must show the retry screen instead.
            if (!isNotFound(err)) throw err;
            const profileResponse = await getWithRetry(`${API}/profile?user_id=${userId}`);
            profileData = profileResponse.data;
            recipeCache.setProfile(userId, profileData);
          }
        }
        
        setProfile(profileData);
        setResolvedUserId(actualUserId);

        // Check cache for recipes
        const recipeCacheKey = `public:${actualUserId}`;
        const cachedRecipeList = recipeCache.getList(recipeCacheKey);
        if (cachedRecipeList) {
          setRecipes(cachedRecipeList);
          setFilteredRecipes(cachedRecipeList);
        } else {
          // Fetch PUBLIC recipes only using the username endpoint (filters out private recipes)
          const username = profileData.username || userId;
          const recipesResponse = await getWithRetry(`${API}/u/${username}/recipes`);
          setRecipes(recipesResponse.data);
          setFilteredRecipes(recipesResponse.data);
          // Cache the list (with slug keys so recipe pages open instantly)
          recipeCache.setList(recipeCacheKey, recipesResponse.data, profileData.username);
        }
        
        // Fetch categories for this user (public access)
        try {
          const categoriesResponse = await axios.get(`${API}/categories?user_id=${actualUserId}`);
          const sortedCategories = categoriesResponse.data.sort((a, b) => (a.order || 0) - (b.order || 0));
          recipeCache.setList(`categories:${actualUserId}`, sortedCategories);
          setCategories(sortedCategories);
        } catch (error) {
          console.error("Failed to fetch categories", error);
          // Set default categories if fetch fails
          setCategories([
            { id: 'breakfast', name: 'Breakfast', order: 0 },
            { id: 'lunch', name: 'Lunch', order: 1 },
            { id: 'dinner', name: 'Dinner', order: 2 }
          ]);
        }
      } catch (error) {
        console.error("Failed to fetch public collection", error);
        // Only a genuine 404 means the collection doesn't exist
        if (isNotFound(error)) {
          setNotFound(true);
        } else {
          setLoadError(true);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchPublicData();
  }, [userId, retryKey, authResolved]);

  useEffect(() => {
    let filtered = recipes;
    
    const hasSearch = searchQuery.trim();
    
    if (selectedCategory !== "all" && !hasSearch) {
      filtered = filtered.filter(r => 
        r.categories && r.categories.some(cat => cat.toLowerCase() === selectedCategory.toLowerCase())
      );
    }
    
    if (hasSearch) {
      filtered = filterRecipesBySearch(filtered, searchQuery);
    }
    
    setFilteredRecipes(filtered);
  }, [selectedCategory, searchQuery, recipes]);

  // Logged-in owner viewing their own page: show the full editable
  // collection (same view as /recipes) so /username is their homepage.
  if (authResolved && isOwnPage(userId)) {
    return <RecipeCollection />;
  }

  if (!loading && loadError) {
    return (
      <NotFound
        title="Trouble loading this collection"
        message="The collection is safe — we just couldn't reach it. Check your connection and try again."
        onRetry={() => {
          setLoadError(false);
          setLoading(true);
          setRetryKey((k) => k + 1);
        }}
      />
    );
  }

  if (!loading && notFound) {
    return (
      <NotFound
        title="Collection not found"
        message="This collection doesn't exist or the link may be broken."
      />
    );
  }

  if (!authResolved || loading) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: '#EEE7D8' }}>
        <div className="max-w-7xl mx-auto px-4 py-8 md:p-8">
          {/* Header Skeleton */}
          <div className="mb-8">
            <div className="flex flex-col md:flex-row md:justify-between md:items-start mb-4 gap-4">
              {/* Title skeleton */}
              <div className="h-10 w-48 rounded" style={{ backgroundColor: '#D6D0C2' }} />
              <div className="flex gap-3 w-full md:w-auto">
                {/* Button skeletons */}
                <div className="h-10 w-32 rounded flex-1 md:flex-initial" style={{ backgroundColor: '#F3EEE4' }} />
                <div className="h-10 w-28 rounded flex-1 md:flex-initial" style={{ backgroundColor: '#F3EEE4' }} />
              </div>
            </div>
            
            {/* Profile Section Skeleton */}
            <div className="flex items-start gap-4 mb-8">
              {/* Avatar skeleton */}
              <div className="w-16 h-16 rounded-full" style={{ backgroundColor: '#D6D0C2' }} />
              <div className="flex-1">
                {/* Name skeleton */}
                <div className="h-6 w-40 rounded mb-2" style={{ backgroundColor: '#D6D0C2' }} />
                {/* Bio skeleton */}
                <div className="h-4 w-64 rounded mb-1" style={{ backgroundColor: '#D6D0C2' }} />
                <div className="h-4 w-48 rounded" style={{ backgroundColor: '#D6D0C2' }} />
              </div>
            </div>
          </div>

          {/* Category Filters Skeleton */}
          <div className="flex gap-3 mb-8 overflow-x-auto pb-2">
            {[1, 2, 3, 4, 5].map(i => (
              <div key={i} className="h-8 w-20 rounded-full flex-shrink-0" style={{ backgroundColor: '#F3EEE4' }} />
            ))}
          </div>

          {/* Recipe Cards Skeleton - Desktop (mixed heights, like real cards) */}
          <RecipeCollectionGrid>
            {[true, false, true, false, false, true].map((withImage, i) => (
              <RecipeCardSkeleton
                key={i}
                withImage={withImage}
              />
            ))}
          </RecipeCollectionGrid>

          {/* Recipe Cards Skeleton - Mobile */}
          <div className="md:hidden space-y-6">
            {[true, false, false, true].map((withImage, i) => (
              <RecipeCardSkeleton key={i} withImage={withImage} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  const handleCopyLink = () => {
    // Simply copy the current page URL
    navigator.clipboard.writeText(window.location.href);
    toast.success('Link copied to clipboard!');
  };

  const handleRecipeClick = (recipe) => {
    // Use username and slug if available
    if (profile.username && recipe.slug) {
      navigate(`/${profile.username}/${recipe.slug}`);
    } else {
      navigate(`/${resolvedUserId || userId}/${recipe.id}`);
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#EEE7D8' }}>
      <div className="max-w-7xl mx-auto px-4 py-8 md:p-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-wrap items-center justify-between gap-y-4 mb-4 lg:flex-nowrap lg:items-start">
            <h1 className={`order-2 w-full font-bold ${isMobile ? 'text-4xl' : 'text-4xl'} lg:order-1 lg:w-auto`} style={{ color: '#51120A', fontFamily: "'Instrument Serif', serif" }} data-testid="page-title">
              {getCollectionDisplayTitle(profile)}
            </h1>
            <div className="order-1 flex w-full items-center justify-start gap-3 pb-2.5 lg:order-2 lg:w-auto lg:justify-end lg:pb-0">
              <button
                type="button"
                onClick={handleCopyLink}
                aria-label="Copy link to this collection"
                title="Copy Link"
                className="copylink-btn flex h-10 w-10 items-center justify-center rounded focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#51120A] lg:hidden"
                style={{ border: 'none', backgroundColor: 'transparent', padding: 0, cursor: 'pointer' }}
                data-testid="copy-collection-link-btn"
              >
                <span
                  className="copylink-ring flex items-center justify-center transition-colors"
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    border: '1px solid #51120A',
                    flexShrink: 0
                  }}
                >
                  <Link className="h-4 w-4" strokeWidth={2.25} style={{ color: '#51120A' }} aria-hidden="true" />
                </span>
              </button>
              <Button
                type="button"
                onClick={() => navigate('/')}
                variant="outline"
                className="h-10 px-4 hover-blush transition-colors lg:hidden"
                style={{ borderColor: '#51120A', color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif", borderWidth: '1px' }}
                data-testid="create-own-collection-btn"
              >
                Create your own
              </Button>
              <div className="hidden gap-3 lg:flex">
              <Button 
                onClick={() => navigate('/')}
                variant="outline"
                className="focus:outline-none focus:ring-0 flex items-center justify-center gap-2 hover-blush transition-colors"
                style={{ borderColor: '#51120A', color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif", borderWidth: '1px' }}
              >
                Create Your Own
              </Button>
              <Button 
                onClick={handleCopyLink}
                className="focus:outline-none focus:ring-0 flex items-center justify-center flex-1 md:flex-initial"
                style={{ backgroundColor: '#51120A', borderColor: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif" }}
              >
                Share
              </Button>
              </div>
            </div>
          </div>
          
          {/* Profile Section - Read Only (No Card Container).
              Always shows a headshot: the real photo, or a blank placeholder
              so the space (and the nudge to add a photo) persists. */}
          {(
            <div className="flex flex-col items-start gap-3 mb-6 md:flex-row md:items-start md:gap-4 md:mb-8">
              {profile.creator_photo_url ? (
                <img 
                  src={profile.creator_photo_url} 
                  alt={profile.creator_name || "Creator"}
                  className="w-16 h-16 md:w-20 md:h-20 rounded-full object-cover"
                />
              ) : (
                <div
                  className="w-16 h-16 md:w-20 md:h-20 rounded-full overflow-hidden flex-shrink-0"
                  data-testid="creator-photo-placeholder"
                >
                  <img src="/avatar-smiley.svg" alt="" className="w-full h-full" draggable="false" />
                </div>
              )}
              <div className="w-full md:flex-1">
                {profile.creator_name && (
                  <h3 className="text-xl font-bold mb-2" style={{ color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif" }}>
                    {profile.creator_name}
                  </h3>
                )}
                {profile.bio && (
                  <p className="md:max-w-[75%]" style={{ color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif" }}>
                    {profile.bio}
                  </p>
                )}
                {/* Social Links Tags */}
                {profile.social_links && Object.keys(profile.social_links).filter(key => profile.social_links[key]).length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-3">
                    {Object.entries(profile.social_links).filter(([_, url]) => url).map(([platform, url]) => {
                      // Extract handle or title from URL
                      const getDisplayText = (platform, url) => {
                        try {
                          const urlObj = new URL(url);
                          const pathname = urlObj.pathname;
                          
                          if (platform === 'instagram' || platform === 'tiktok' || platform === 'x' || platform === 'threads') {
                            const handle = pathname.split('/').filter(p => p)[0];
                            return handle ? `@${handle}` : platform;
                          } else if (platform === 'substack') {
                            const subdomain = urlObj.hostname.split('.')[0];
                            return subdomain !== 'www' ? subdomain : 'Newsletter';
                          } else if (platform === 'youtube') {
                            const parts = pathname.split('/').filter(p => p);
                            return parts.length > 0 ? parts[parts.length - 1] : 'YouTube';
                          } else if (platform === 'website') {
                            return urlObj.hostname.replace('www.', '');
                          } else {
                            return platform.charAt(0).toUpperCase() + platform.slice(1);
                          }
                        } catch {
                          return platform.charAt(0).toUpperCase() + platform.slice(1);
                        }
                      };
                      
                      const getIcon = (platform) => {
                        const iconSize = 14;
                        const iconColor = '#51120A';
                        switch(platform) {
                          case 'instagram': return <Instagram size={iconSize} color={iconColor} />;
                          case 'tiktok': return <TikTokIcon size={iconSize} color={iconColor} />;
                          case 'youtube': return <Youtube size={iconSize} color={iconColor} />;
                          case 'facebook': return <Facebook size={iconSize} color={iconColor} />;
                          case 'x': return <XIcon size={iconSize} color={iconColor} />;
                          case 'threads': return <ThreadsIcon size={iconSize} color={iconColor} />;
                          case 'substack': return <SubstackIcon size={iconSize} color={iconColor} />;
                          case 'website': return <Globe size={iconSize} color={iconColor} />;
                          default: return null;
                        }
                      };
                      
                      return (
                        <a
                          key={platform}
                          href={normalizeSocialUrl(url)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="px-3 py-1 rounded text-sm transition-colors hover:opacity-80 flex items-center gap-1.5"
                          style={{ backgroundColor: '#F3EEE4', color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif", textDecoration: 'none' }}
                        >
                          {getIcon(platform)}
                          {getDisplayText(platform, url)}
                        </a>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Pinned Recipes Section - Above Filters */}
        {recipes.filter(r => r.pinned).length > 0 && (
          <div className="mb-8 mt-5">
            <h2 className="text-xl font-bold mb-6" style={{ color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif" }}>
              Pinned Recipes
            </h2>
            {/* Desktop: 3 columns, filled left-to-right */}
            <RecipeCollectionGrid className="mb-8">
              {recipes.filter(r => r.pinned).map(recipe => (
                <div key={recipe.id}>
                  <RecipeCard
                    recipe={recipe}
                    isPublic={true}
                    onBeforeNavigate={saveReturnState}
                    userId={resolvedUserId || userId}
                    username={profile.username}
                    onSave={isOwnCollection ? undefined : handleSaveRecipe}
                    isSaved={savedOriginalIds.has(recipe.id)}
                  />
                </div>
              ))}
            </RecipeCollectionGrid>
            
            {/* Mobile: Single column for pinned */}
            <div className="md:hidden block space-y-6">
              {recipes.filter(r => r.pinned).map(recipe => (
                <RecipeCard
                  key={recipe.id}
                  recipe={recipe}
                  isPublic={true}
                    onBeforeNavigate={saveReturnState}
                  userId={resolvedUserId || userId}
                  username={profile.username}
                  onSave={isOwnCollection ? undefined : handleSaveRecipe}
                  isSaved={savedOriginalIds.has(recipe.id)}
                />
              ))}
            </div>
          </div>
        )}

        {/* Empty collection: same sketched collage as the owner's first-login
            empty state, but read-only copy and no category/search toolbar. */}
        {recipes.length === 0 && (
          <EmptyCollectionState heading="No recipes added yet" />
        )}

        {/* Category Filters with Search Bar (hidden when there's nothing to filter) */}
        {recipes.length === 0 ? null : isMobile ? (
          /* Mobile Layout - Stacked */
          <div className="mb-8">
            {/* Category filters - left-aligned, wrapping on two lines */}
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2 mb-4">
              <span className="inline-flex items-center gap-3">
              <button
                onClick={() => setSelectedCategory("all")}
                className={`transition-colors ${selectedCategory === "all" ? 'underline' : 'hover:text-[#875C66]'}`}
                style={{ 
                  color: selectedCategory === "all" ? '#875C66' : '#51120A',
                  fontFamily: "'Bricolage Grotesque', sans-serif",
                  textDecorationColor: selectedCategory === "all" ? '#875C66' : 'transparent',
                  textUnderlineOffset: '4px',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '16px',
                  textAlign: 'left'
                }}
              >
                All Recipes
              </button>
              <span style={{ color: '#D6D0C2', fontSize: '20px' }}>|</span>
              </span>
              {categories.map((cat, index) => (
                <span key={cat.id} className="inline-flex items-center gap-3">
                  <button
                    onClick={() => setSelectedCategory(cat.name.toLowerCase())}
                    className={`transition-colors ${selectedCategory === cat.name.toLowerCase() ? 'underline' : 'hover:text-[#875C66]'}`}
                    style={{ 
                      color: selectedCategory === cat.name.toLowerCase() ? '#875C66' : '#51120A',
                      fontFamily: "'Bricolage Grotesque', sans-serif",
                      textDecorationColor: selectedCategory === cat.name.toLowerCase() ? '#875C66' : 'transparent',
                      textUnderlineOffset: '4px',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '16px',
                      textAlign: 'left'
                    }}
                    data-testid={`category-filter-${cat.id}`}
                  >
                    {cat.name}
                  </button>
                  {index < categories.length - 1 && (
                    <span style={{ color: '#D6D0C2', fontSize: '20px' }}>|</span>
                  )}
                </span>
              ))}
            </div>

            {/* Search Bar - full width */}
            <Input
              placeholder="Search Recipes"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full border-0 border-b rounded-none focus:outline-none focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0"
              style={{ 
                borderColor: '#51120A', 
                borderBottomWidth: '1px',
                borderBottomColor: '#51120A',
                paddingLeft: '0', 
                backgroundColor: 'transparent',
                color: '#51120A',
                fontSize: '16px',
                fontFamily: "'Bricolage Grotesque', sans-serif",
                boxShadow: 'none'
              }}
              data-testid="search-input"
            />
          </div>
        ) : (
          /* Desktop/Tablet Layout */
          <div className="flex items-center justify-between mb-8 gap-10">
            {/* Category Filters */}
            <div className="flex items-center gap-4 flex-wrap">
              <span className="inline-flex items-center gap-4">
              <button
                onClick={() => setSelectedCategory("all")}
                className={`transition-colors ${selectedCategory === "all" ? 'underline' : 'hover:text-[#875C66]'}`}
                style={{ 
                  color: selectedCategory === "all" ? '#875C66' : '#51120A',
                  fontFamily: "'Bricolage Grotesque', sans-serif",
                  textDecorationColor: selectedCategory === "all" ? '#875C66' : 'transparent',
                  textUnderlineOffset: '4px',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '16px'
                }}
              >
                All Recipes
              </button>
              <span style={{ color: '#D6D0C2', fontSize: '20px' }}>|</span>
              </span>
              {categories.map((cat, index) => (
                <span key={cat.id} className="inline-flex items-center gap-4">
                  <button
                    onClick={() => setSelectedCategory(cat.name.toLowerCase())}
                    className={`transition-colors ${selectedCategory === cat.name.toLowerCase() ? 'underline' : 'hover:text-[#875C66]'}`}
                    style={{ 
                      color: selectedCategory === cat.name.toLowerCase() ? '#875C66' : '#51120A',
                      fontFamily: "'Bricolage Grotesque', sans-serif",
                      textDecorationColor: selectedCategory === cat.name.toLowerCase() ? '#875C66' : 'transparent',
                      textUnderlineOffset: '4px',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '16px'
                    }}
                    data-testid={`category-filter-${cat.id}`}
                  >
                    {cat.name}
                  </button>
                  {index < categories.length - 1 && (
                    <span style={{ color: '#D6D0C2', fontSize: '20px' }}>|</span>
                  )}
                </span>
              ))}
            </div>

            {/* Search Bar - Right Aligned */}
            <Input
              placeholder="Search Recipes"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-64 border-0 border-b rounded-none focus:outline-none focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0"
              style={{ 
                borderColor: '#51120A', 
                borderBottomWidth: '1px',
                borderBottomColor: '#51120A',
                paddingLeft: '0', 
                backgroundColor: 'transparent',
                color: '#51120A',
                fontSize: '16px',
                fontFamily: "'Bricolage Grotesque', sans-serif",
                boxShadow: 'none'
              }}
              data-testid="search-input"
            />
          </div>
        )}

        {/* Recipe Grid - Natural-height columns, assigned left-to-right */}
        <RecipeCollectionGrid
          data-testid="recipes-grid"
        >
          {filteredRecipes.map(recipe => (
            <div key={recipe.id}>
              <RecipeCard
                recipe={recipe}
                isPublic={true}
                    onBeforeNavigate={saveReturnState}
                userId={resolvedUserId || userId}
                username={profile.username}
                onSave={isOwnCollection ? undefined : handleSaveRecipe}
                isSaved={savedOriginalIds.has(recipe.id)}
              />
            </div>
          ))}
        </RecipeCollectionGrid>
        
        {/* Mobile: Single column */}
        <div className="md:hidden block space-y-6" data-testid="recipes-grid-mobile">
          {filteredRecipes.map(recipe => (
            <RecipeCard
              key={recipe.id}
              recipe={recipe}
              isPublic={true}
                    onBeforeNavigate={saveReturnState}
              userId={resolvedUserId || userId}
              username={profile.username}
              onSave={isOwnCollection ? undefined : handleSaveRecipe}
              isSaved={savedOriginalIds.has(recipe.id)}
            />
          ))}
        </div>

        {recipes.length > 0 && filteredRecipes.length === 0 && (
          <div className="text-center py-12" data-testid="no-recipes-message">
            <p style={{ color: '#51120A' }} className="text-lg">No recipes found</p>
          </div>
        )}
      </div>

      {/* CTA Banner */}
      <PublicCTA />

      <BetaForm
        open={showWaitlist}
        onOpenChange={setShowWaitlist}
        pendingSaveRecipe={pendingSaveRecipe}
        onLoggedInForSave={(r) => setSaveOverlayRecipe(r)}
      />
      {saveOverlayRecipe && (
        <SaveRecipeOverlay
          original={saveOverlayRecipe}
          onClose={() => setSaveOverlayRecipe(null)}
        />
      )}
    </div>
  );
};

export default PublicCollectionView;
